// Moteur family-chart (ISC, D3) : arbre interactif moderne. Cartes en SVG natif
// (CardSvg) pour un export propre. Données au format { id, data, rels }.
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
// @ts-ignore — family-chart n'expose pas de types complets pour le default export
import f3 from 'family-chart';
import 'family-chart/styles/family-chart.css';
import { Membre, Relation } from '../../types';
import { toPersonNodes } from '../../utils/genealogyModel';
import { exportSvgToPdf, exportSvgToPng } from '../../utils/exportTree';
import { EngineExportHandle } from './engineTypes';

interface Props {
  membres: Membre[];
  relations: Relation[];
}

const year = (d?: string) => (d ? String(new Date(d).getFullYear()) : '');

const FamilyChartView = forwardRef<EngineExportHandle, Props>(function FamilyChartView(
  { membres, relations }, ref,
) {
  const contRef = useRef<HTMLDivElement>(null);

  const svgEl = () => contRef.current?.querySelector('svg.main_svg') as SVGSVGElement | null;
  useImperativeHandle(ref, () => ({
    exportPdf: () => { const el = svgEl(); return el ? exportSvgToPdf(el, 'arbre-familychart.pdf') : undefined; },
    exportPng: () => { const el = svgEl(); return el ? exportSvgToPng(el, 'arbre-familychart.png', 2) : undefined; },
  }));

  useEffect(() => {
    const cont = contRef.current;
    if (!cont) return;
    const nodes = toPersonNodes(membres, relations);
    cont.innerHTML = '';
    if (!nodes.length) return;
    const data = nodes.map(n => ({
      id: String(n.id),
      data: {
        'first name': n.prenom,
        'last name': n.nom,
        birthday: year(n.date_naissance),
        gender: n.sexe === 'Femme' ? 'F' : 'M',
      },
      // family-chart 0.9 attend rels.parents (tableau), PAS father/mother.
      rels: {
        parents: [n.father, n.mother].filter((v): v is number => v != null).map(String),
        spouses: n.spouses.map(String),
        children: n.children.map(String),
      },
    }));
    // family-chart centre l'arbre sur data[0]. On met en tête la personne ayant
    // le PLUS de descendants (l'apex), sinon il se centre sur une feuille et
    // n'affiche qu'un couple.
    const byId = new Map(nodes.map(n => [n.id, n]));
    const descCount = (id: number) => {
      const seen = new Set<number>(); const stack = [id];
      while (stack.length) {
        const x = stack.pop()!;
        (byId.get(x)?.children || []).forEach(c => { if (!seen.has(c)) { seen.add(c); stack.push(c); } });
      }
      return seen.size;
    };
    let mainId = nodes[0].id, best = -1;
    nodes.forEach(n => { const d = descCount(n.id); if (d > best) { best = d; mainId = n.id; } });
    const mainStr = String(mainId);
    data.sort((a, b) => (a.id === mainStr ? -1 : b.id === mainStr ? 1 : 0));
    // eslint-disable-next-line no-console
    console.log('[DIAG] family-chart : principal =', data[0]?.id, '(', best, 'descendants ) ; 72 reçus');
    try {
      const anyF3 = f3 as any;
      const chart = anyF3.createChart(cont, data);
      chart.setTransitionTime(0);
      const card = chart.setCard(anyF3.CardSvg);
      card.setCardDisplay([['first name', 'last name'], ['birthday']]);
      if (card.setMiniTree) card.setMiniTree(false);
      // tree_position:'fit' cadre tout l'arbre (sinon zoom initial sur la racine).
      chart.updateTree({ initial: true, tree_position: 'fit' });
    } catch { /* rendu impossible */ }
  }, [membres, relations]);

  return <div ref={contRef} className="f3" style={{ width: '100%', height: 460 }} />;
});

export default FamilyChartView;
